import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Instagram, MessageCircle, Send, Loader2, Check } from "lucide-react";
import { DESTINOS, EMAIL, INSTAGRAM, TRANSPORTES, waLink, WHATSAPP } from "./data";
import { Reveal, Overline } from "./Reveal";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { nombre: "", email: "", telefono: "", transporte: "", plan: "", destino: "", fechas: "", viajeros: "", mensaje: "" };
const PLANES = ["Esencial (Gs. 100.000)", "Completo (Gs. 250.000)", "Premium (Gs. 400.000)", "Avión o combinado (5% del presupuesto)"];
const inputCls = "w-full rounded-2xl border border-selva/15 bg-white px-5 py-4 text-base text-tinta placeholder:text-tinta/45 outline-none transition-[border-color,box-shadow] focus:border-mar focus:shadow-[0_0_0_4px_rgba(27,42,92,0.18)]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-selva/80">{label}</span>
    {children}
  </label>
);

const waMessage = (f) =>
  `¡Hola JahaBrasilpe! Soy ${f.nombre}. Quiero planificar un viaje${f.destino ? ` a ${f.destino}` : " a Brasil"}${f.transporte ? `, ${f.transporte.toLowerCase()}` : ""}${f.plan ? `, plan ${f.plan}` : ""}${f.fechas ? ` (${f.fechas})` : ""}${f.viajeros ? `, somos ${f.viajeros}` : ""}. ${f.mensaje}`.trim();

export const Contact = ({ prefill }) => {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (!prefill) return;
    const { n, ...values } = prefill;
    setSent(null);
    setF((p) => ({ ...p, ...values }));
  }, [prefill]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!API) { toast.error("No pudimos enviar. Escríbenos por WhatsApp."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, f);
      setSent({ ...f, autoreply: !!data?.autoreply_sent });
      setF(EMPTY);
      toast.success("¡Solicitud recibida! Te contactaremos muy pronto.");
    } catch (err) {
      toast.error("Revisa tus datos o escríbenos por WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="bg-arena pb-24 pt-12 lg:pb-36">
      <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-16">
        <Reveal className="lg:col-span-7">
          <div className="rounded-[2rem] bg-[#EFE8D6] p-6 sm:p-10">
            <Overline>Contacto</Overline>
            <h2 className="font-display text-4xl font-extrabold leading-[0.95] tracking-tight text-selva sm:text-5xl">
              Cuéntanos tu <span className="font-serif-i font-normal text-mar">próximo verano.</span>
            </h2>
            {sent ? (
              <div data-testid="contact-success" className="mt-10 rounded-3xl bg-mar p-8 text-arena">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sol text-selva"><Check className="h-6 w-6" /></span>
                <h3 className="mt-6 font-display text-3xl font-bold">¡Gracias, {sent.nombre}!</h3>
                <p className="mt-3 text-arena/85">Recibimos tu solicitud.{sent.autoreply ? ` Te enviamos un email de confirmación a ${sent.email}.` : ""} ¿Querés acelerar? Envianos el resumen por WhatsApp.</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a data-testid="contact-success-whatsapp" href={waLink(waMessage(sent))} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-bold text-selva"><MessageCircle className="h-5 w-5" /> Continuar por WhatsApp</a>
                  <button data-testid="contact-new-request" onClick={() => setSent(null)} className="rounded-full border border-arena/40 px-6 py-3 font-bold">Nueva solicitud</button>
                </div>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="mt-10 grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-selva/80">¿Cómo querés viajar?</span>
                  <div className="grid gap-2 sm:grid-cols-3" role="radiogroup">
                    {TRANSPORTES.map((t, i) => (
                      <button type="button" key={t.id} role="radio" aria-checked={f.transporte === t.id} data-testid={`contact-transporte-${i + 1}`}
                        onClick={() => setF((p) => ({ ...p, transporte: p.transporte === t.id ? "" : t.id }))}
                        className={`rounded-2xl border px-4 py-3.5 text-sm font-bold transition-colors ${f.transporte === t.id ? "border-mar bg-mar text-white" : "border-mar/15 bg-white text-mar hover:border-mar/40"}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  {f.transporte.startsWith("Combinado") && (
                    <p data-testid="contact-combinado-note" className="mt-3 rounded-2xl bg-sol/30 px-4 py-3 text-sm text-mar">
                      Viajás en auto hasta Foz do Iguaçu y desde ahí tomás tu vuelo (por ejemplo al Nordeste). Según el horario del vuelo, incluimos 1 noche en Foz.
                    </p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Field label="Plan elegido">
                    <select data-testid="contact-select-plan" value={f.plan} onChange={set("plan")} className={inputCls}>
                      <option value="">Aún no lo sé / quiero asesoramiento</option>
                      <optgroup label="Viaje en auto">
                        {PLANES.slice(0, 3).map((p) => <option key={p} value={p}>{p}</option>)}
                      </optgroup>
                      <optgroup label="Avión o combinado">
                        <option value={PLANES[3]}>{PLANES[3]}</option>
                      </optgroup>
                    </select>
                  </Field>
                </div>
                <Field label="Nombre *"><input data-testid="contact-input-nombre" required minLength={2} value={f.nombre} onChange={set("nombre")} placeholder="Tu nombre" className={inputCls} /></Field>
                <Field label="Email *"><input data-testid="contact-input-email" required type="email" value={f.email} onChange={set("email")} placeholder="tu@email.com" className={inputCls} /></Field>
                <Field label="Teléfono / WhatsApp"><input data-testid="contact-input-telefono" value={f.telefono} onChange={set("telefono")} placeholder="+595 ..." className={inputCls} /></Field>
                <Field label="Destino">
                  <select data-testid="contact-select-destino" value={f.destino} onChange={set("destino")} className={inputCls}>
                    <option value="">Aún no lo sé</option>
                    {DESTINOS.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                    <option value="Varios destinos">Varios destinos</option>
                  </select>
                </Field>
                <Field label="Fechas estimadas"><input data-testid="contact-input-fechas" value={f.fechas} onChange={set("fechas")} placeholder="Ej: enero 2027" className={inputCls} /></Field>
                <Field label="Viajeros"><input data-testid="contact-input-viajeros" value={f.viajeros} onChange={set("viajeros")} placeholder="Ej: 2 adultos" className={inputCls} /></Field>
                <div className="sm:col-span-2">
                  <Field label="Mensaje"><textarea data-testid="contact-input-mensaje" rows={4} value={f.mensaje} onChange={set("mensaje")} placeholder="¿Qué te gustaría vivir en Brasil?" className={inputCls} /></Field>
                </div>
                <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row">
                  <button data-testid="contact-form-submit-button" disabled={loading} type="submit"
                    className="group flex flex-1 items-center justify-center gap-2 rounded-full bg-mar px-7 py-4 font-bold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-70">
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 transition-transform group-hover:translate-x-1" />} Enviar solicitud
                  </button>
                  <a data-testid="contact-form-whatsapp-button" href={waLink(f.nombre ? waMessage(f) : undefined)} target="_blank" rel="noopener noreferrer"
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-4 font-bold text-selva transition-transform duration-300 hover:-translate-y-0.5">
                    <MessageCircle className="h-5 w-5" /> Enviar por WhatsApp
                  </a>
                </div>
              </form>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <ContactCard />
        </Reveal>
      </div>
    </section>
  );
};

const ContactCard = () => (
  <aside data-testid="contact-card" className="relative flex h-full min-h-[520px] flex-col justify-between overflow-hidden rounded-[2rem] bg-selva p-8 text-arena sm:p-10">
    <img src="/img/copa.jpg" alt="Playa de Copacabana" className="absolute inset-0 h-full w-full object-cover opacity-35" />
    <div className="absolute inset-0 bg-gradient-to-b from-selva/60 to-selva" />
    <div className="relative">
      <p className="text-xs font-bold uppercase tracking-[0.28em] text-sol">Respuesta directa</p>
      <h3 className="mt-4 font-display text-4xl font-extrabold leading-tight">¿Prefieres hablar ya?</h3>
      <p className="mt-3 text-arena/85">Escríbenos y una persona del equipo te responde.</p>
    </div>
    <div className="relative mt-10 space-y-3">
      <a data-testid="contact-card-whatsapp" href={waLink()} target="_blank" rel="noopener noreferrer"
        className="flex items-center justify-between rounded-2xl bg-[#25D366] px-6 py-5 font-bold text-selva transition-transform duration-300 hover:-translate-y-1">
        <span className="flex items-center gap-3"><MessageCircle className="h-5 w-5" /> WhatsApp</span><span className="text-sm">+{WHATSAPP.slice(0, 3)} {WHATSAPP.slice(3, 6)} {WHATSAPP.slice(6)}</span>
      </a>
      <a data-testid="contact-card-email" href={`mailto:${EMAIL}`}
        className="flex items-center gap-3 rounded-2xl border border-arena/25 bg-arena/5 px-6 py-5 font-semibold backdrop-blur transition-colors duration-300 hover:bg-arena/15">
        <Mail className="h-5 w-5 text-sol" /> <span className="break-all">{EMAIL}</span>
      </a>
      <a data-testid="contact-card-instagram" href={`https://instagram.com/${INSTAGRAM}`} target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-3 rounded-2xl border border-arena/25 bg-arena/5 px-6 py-5 font-semibold backdrop-blur transition-colors duration-300 hover:bg-arena/15">
        <Instagram className="h-5 w-5 text-sol" /> @{INSTAGRAM}
      </a>
    </div>
  </aside>
);
